import { formatMoney, toFixed } from "../../number-transforms";
import {
  BlendedLTCCalc,
  CashToClose,
  FinalLoanAmount,
  GenericFixAndFlipLTCCalulationMap,
  LoanCosts,
  LTCCalcOnPurchaseRehab,
  LTVARVCalc,
  Payments,
  RateFeeLLPA,
} from "./short-term-calculation.model";

export const blendedLTCCalc: GenericFixAndFlipLTCCalulationMap<BlendedLTCCalc>[] =
  [
    {
      label: "Total Cost",
      accessor: "totalCost",
      transformer: (val: string | number) => `$ ${formatMoney(val, 0)}`,
    },
    {
      label: "Maximum Blended LTC ratio",
      accessor: "maxBlendedLTCRatio",
      transformer: (val: string | number) => `${toFixed(val, 2)} %`,
    },
    {
      label: "Blended LTC Maximum Loan",
      accessor: "blendedLTCMaxLoan",
      transformer: (val: string | number) => `$ ${formatMoney(val, 0)}`,
    },
  ];

export const ltcCalcOnPurchaseRehab: GenericFixAndFlipLTCCalulationMap<LTCCalcOnPurchaseRehab>[] =
  [
    {
      label: "Max LTC on Purchase",
      accessor: "maxLTCOnPurchase",
      transformer: (val: string | number) => `${toFixed(val, 2)} %`,
    },
    {
      label: "LTC Construction Budget",
      accessor: "LTCConBudget",
      transformer: (val: string | number) => `${toFixed(val, 2)} %`,
    },
    {
      label: "Purchase + Rehab Maximum Loan",
      accessor: "ltcCalcRehabMaxLoan",
      transformer: (val) => `$ ${formatMoney(val, 0)}`,
    },
  ];

export const ltvARVCalc: GenericFixAndFlipLTCCalulationMap<LTVARVCalc>[] = [
  {
    label: "After completed value",
    accessor: "afterCompletedVal",
    transformer: (val: string | number) => `$ ${formatMoney(val, 0)}`,
  },
  {
    label: "Maximum LTV ratio",
    accessor: "maxLTVRatio",
    transformer: (val: string | number) => `${toFixed(val, 2)} %`,
  },
  {
    label: "LTV Maximum Loan",
    accessor: "LTVMaxLoan",
    transformer: (val: string | number) => `$ ${formatMoney(val, 0)}`,
  },
];

export const cashToClose: GenericFixAndFlipLTCCalulationMap<CashToClose>[] = [
  {
    label: "Purchase",
    accessor: "purchase",
    transformer: (val: string | number) => `$ ${formatMoney(val, 0)}`,
  },
  {
    label: "Existing Payoff",
    accessor: "existingPayoff",
    transformer: (val: string | number) => `$ ${formatMoney(val, 0)}`,
  },
  {
    label: "Construction Budget",
    accessor: "conBudget",
    transformer: (val: string | number) => `$ ${formatMoney(val, 0)}`,
  },
  {
    label: "Total Loan Fees & Costs",
    accessor: "totalLoanFeesCost",
    transformer: (val: string | number) => `$ ${formatMoney(val, 0)}`,
  },
  {
    label: "Estimated Loan Amount",
    accessor: "estimatedLoanAmt",
    transformer: (val: string | number) => `$ ${formatMoney(val, 0)}`,
  },
  {
    label: "Estimated Cash Due From Borrower at Close",
    labelAlt: "Estimated Cash Due To Borrower at Close",
    showLabelAltOnCondition: (val) => val > 0,
    accessor: "estimatedCashAtClose",
    transformer: (val: string | number) =>
      `$ ${formatMoney(Math.abs(+val), 0)}`,
  },
];

export const loanCosts: GenericFixAndFlipLTCCalulationMap<LoanCosts>[] = [
  {
    label: "Broker Fee",
    accessor: "brokerFee",
    transformer: (val: string | number) => `$ ${formatMoney(val, 0)}`,
  },
  {
    label: "Interest Reserve",
    accessor: "interestReserve",
    transformer: (val: string | number) => `$ ${formatMoney(val, 0)}`,
  },
  {
    label: "Origination Fee",
    accessor: "originationFee",
    transformer: (val: string | number) => `$ ${formatMoney(val, 0)}`,
  },
  {
    label: "Deferred Origination Fee",
    accessor: "exitFee",
    transformer: (val: string | number) => `$ ${formatMoney(val, 0)}`,
  },
  {
    label: "Underwriting and Processing Fee",
    accessor: "processingFee",
    transformer: (val: string | number) => `$ ${formatMoney(val, 0)}`,
  },
  {
    label: "Broker Processing Fee",
    accessor: "brokerProcessingFee",
    transformer: (val) => {
      return val === 0 ? "N/A" : `$ ${formatMoney(val, 0)}`;
    },
  },
  {
    label: "Valuation Fee Paid Outside Closing",
    accessor: "appraisalFee",
    transformer: (val: number | string) => {
      return val === 0 ? "TBD" : `$ ${formatMoney(val, 2)}`;
    },
  },
  {
    label: "Total Loan Costs & Fees",
    accessor: "totalLoanCostFees",
    transformer: (val: string | number) => `$ ${formatMoney(val, 0)}`,
  },
];

export const rateFeesLLPA: GenericFixAndFlipLTCCalulationMap<RateFeeLLPA>[] = [
  {
    label: "Base Rate",
    accessor: "baseRate",
    transformer: (val: string | number) => `${toFixed(val, 2)} %`,
  },
  {
    label: "Total Rate LLPA's",
    accessor: "totalRateLLPA",
    transformer: (val: string | number) => `${toFixed(val, 2)} %`,
  },
  {
    label: "Final Rate",
    accessor: "finalRate",
    canOverride: true,
    transformer: (val: string | number) => `${toFixed(val, 2)} %`,
  },
  {
    label: "Base Orig Fee",
    accessor: "baseOrigFee",
    transformer: (val: string | number) => `${toFixed(val, 2)} %`,
  },
  {
    label: "Total Orig Fee LLPA's",
    accessor: "totalOrigFeeLLPA",
    transformer: (val: string | number) => `${toFixed(val, 2)} %`,
  },
  {
    label: "Final Orig Fee",
    accessor: "finalOrigFee",
    canOverride: true,
    transformer: (val: string | number) => `${toFixed(val, 2)} %`,
  },
  {
    label: "Referral Fee",
    accessor: "referralFee",
    transformer: (val: string | number) => `${toFixed(val, 2)} %`,
  },
];

export const payments: GenericFixAndFlipLTCCalulationMap<Payments>[] = [
  {
    label: "Initial Monthly Payment:",
    accessor: "initMonthlyPayment",
    transformer: (val: string | number) => `$ ${formatMoney(val, 0)}`,
  },
  {
    label: "Payment Fully Drawn",
    accessor: "paymentFullyDrawn",
    transformer: (val: string | number) => `$ ${formatMoney(val, 0)}`,
  },
];

export const finalLoanAmount: GenericFixAndFlipLTCCalulationMap<FinalLoanAmount>[] =
  [
    {
      label: "Total Loan Amount",
      accessor: "loanAmt",
      transformer: (val: string | number) => `$ ${formatMoney(val, 0)}`,
    },
    {
      label: "Initially Funded Loan Amount",
      accessor: "initFundedLoanAmt",
      transformer: (val: string | number) => `$ ${formatMoney(val, 0)}`,
    },
  ];
